/**
 * §7 Advanced: add a slot to a shipped type. The form is a guided
 * constructor for one overlay `slots` entry + its per-language asks.
 * Client-side checks are convenience only (key shape/uniqueness, ask
 * completeness) — the server's validate pass is the gate
 * (overlay_missing_ask_language, overlay_invalid_shape, ...).
 */
import { useState } from 'react'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { deriveKey, KEY_RE } from './deriveKey'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'

/** Mirrors OrderSlotType in backend/app/schemas/order_workflow.py — keep in sync. */
const SLOT_TYPES = [
  'string', 'text', 'integer', 'number', 'boolean',
  'date', 'datetime', 'time', 'enum', 'phone', 'email', 'place', 'window',
] as const

const inputCls =
  'rounded-sm border border-border bg-background p-1 text-xs text-foreground'

export function AddSlotForm({ base }: { base: OrderWorkflowDefinitionJson }) {
  const advanced = useWorkflowConfigStore((s) => s.advanced)
  const overlaySlots = useWorkflowConfigStore((s) => s.overlay.slots)
  const overlayNeeds = useWorkflowConfigStore((s) => s.overlay.needs)
  const addSlot = useWorkflowConfigStore((s) => s.addSlot)

  const [open, setOpen] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [label, setLabel] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [type, setType] = useState<string>('string')
  const [asked, setAsked] = useState<'optional' | 'always' | 'conditional'>('optional')
  const [condSubject, setCondSubject] = useState('')
  const [condOp, setCondOp] = useState<'==' | '!='>('==')
  const [condValue, setCondValue] = useState('')
  const [priority, setPriority] = useState(100)
  const [choicesText, setChoicesText] = useState('')
  const [options, setOptions] = useState<string[]>([])
  const [promptHint, setPromptHint] = useState('')
  const [asks, setAsks] = useState<Record<string, string>>({})

  if (!advanced) return null

  // Overlay-added, non-tombstone slots must be pickable as condition
  // subjects too (§7 flagship flow). A tombstone is exactly {disabled:
  // true}; skip those AND base slots the overlay disables.
  const isTombstone = (v: unknown) =>
    !!v && typeof v === 'object' && Object.keys(v).length === 1 && (v as { disabled?: unknown }).disabled === true

  const subjectSlots: Record<string, { label?: string | null; options?: string[] | null }> = {}
  for (const [k, s] of Object.entries(base.slots ?? {})) {
    if (isTombstone(overlaySlots?.[k])) continue
    const o = overlaySlots?.[k] as
      | { label?: string | null; options?: string[] | null }
      | undefined
    subjectSlots[k] = {
      label: o?.label ?? s.label,
      options: o?.options ?? s.options,
    }
  }
  for (const [k, s] of Object.entries(overlaySlots ?? {})) {
    if (!(k in (base.slots ?? {})) && !isTombstone(s)) {
      subjectSlots[k] = s as { label?: string | null; options?: string[] | null }
    }
  }

  const languages = base.languages ?? []
  const effectiveKey = keyTouched ? key : deriveKey(label)
  const existingKeys = new Set([
    ...Object.keys(base.slots ?? {}),
    ...Object.keys(base.needs ?? {}),
    ...Object.keys(overlaySlots ?? {}),
    ...Object.keys(overlayNeeds ?? {}),
  ])
  const keyTaken = existingKeys.has(effectiveKey)
  const keyInvalid = effectiveKey !== '' && !KEY_RE.test(effectiveKey)
  const advancedOpen = showAdvanced || keyTaken || keyInvalid
  const asksComplete = languages.every((lang) => (asks[lang] ?? '').trim() !== '')
  const condValueHasApostrophe = condValue.includes("'")
  const conditionReady =
    asked !== 'conditional' || (condSubject !== '' && condValue.trim() !== '' && !condValueHasApostrophe)
  // Parse the LIVE choicesText, not the blur-committed `options` state — a
  // user who hasn't blurred the Choices field yet still has typed choices
  // that should count toward enabling Add.
  const enumChoicesReady =
    type !== 'enum' ||
    choicesText.split(',').map((s) => s.trim()).filter(Boolean).length > 0
  const canSubmit =
    effectiveKey !== '' && !keyTaken && !keyInvalid && asksComplete &&
    conditionReady && enumChoicesReady

  const reset = () => {
    setOpen(false); setShowAdvanced(false); setLabel(''); setKey(''); setKeyTouched(false); setType('string')
    setAsked('optional'); setCondSubject(''); setCondOp('=='); setCondValue('')
    setPriority(100); setChoicesText(''); setOptions([]); setPromptHint(''); setAsks({})
  }

  const submit = () => {
    // Build the spec by OMISSION — never write undefined into the overlay
    // (the store's clone() is a JSON round-trip and drops undefined).
    const spec: Record<string, unknown> = {
      type,
      ask_priority: Number.isFinite(priority) ? Math.trunc(priority) : 100
    }
    if (label.trim()) spec.label = label.trim()
    if (asked === 'always') spec.always_required = true
    if (asked === 'conditional') {
      spec.required_when = `${condSubject} ${condOp} '${condValue.trim()}'`
    }
    if (type === 'enum' && options.length > 0) spec.options = options
    if (promptHint.trim()) spec.prompt_hint = promptHint.trim()
    const trimmedAsks = Object.fromEntries(
      languages.map((lang) => [lang, (asks[lang] ?? '').trim()]),
    )
    addSlot(effectiveKey, spec, trimmedAsks)
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        className="rounded-xl border border-dashed border-border text-muted-foreground px-3 py-1.5 text-xs font-semibold transition hover:border-primary hover:bg-primary/10 hover:text-primary"
        onClick={() => setOpen(true)}
      >
        + Add something to collect
      </button>
    )
  }

  const subjectSlot = subjectSlots[condSubject]

  return (
    <div className="rounded-xl border border-border p-3 space-y-2 text-xs text-foreground">
      <p className="font-medium text-foreground">Add something to collect</p>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-1.5">
          Label
          <input aria-label="label" className={`${inputCls} w-44`} value={label}
            onChange={(e) => setLabel(e.target.value)} />
        </label>
        {advancedOpen && (
          <label className="flex items-center gap-1.5">
            Key
            <input aria-label="key" className={`${inputCls} w-36 font-mono`} value={effectiveKey}
              onChange={(e) => { setKey(e.target.value); setKeyTouched(true) }} />
          </label>
        )}
        <label className="flex items-center gap-1.5">
          Type
          <select aria-label="type" className={inputCls} value={type}
            onChange={(e) => setType(e.target.value)}>
            {SLOT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          Ask order
          <input aria-label="ask order" type="number" className={`${inputCls} w-20`}
            value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </label>
        <button
          type="button"
          aria-expanded={advancedOpen}
          className="text-xs text-muted-foreground underline decoration-dotted hover:text-foreground"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          Advanced
        </button>
      </div>
      {keyTaken && <p className="text-destructive">That key already exists in this workflow.</p>}
      {keyInvalid && <p className="text-destructive">Keys are lowercase letters, digits and underscores, starting with a letter.</p>}

      <label className="flex items-center gap-1.5">
        Asked
        <select aria-label="asked" className={inputCls} value={asked}
          onChange={(e) => setAsked(e.target.value as typeof asked)}>
          <option value="optional">Only if the customer brings it up (optional)</option>
          <option value="always">Always</option>
          <option value="conditional">Only under a condition…</option>
        </select>
      </label>
      {asked === 'conditional' && (
        <div className="flex flex-wrap items-center gap-2 pl-4">
          <label className="flex items-center gap-1.5">
            When
            <select aria-label="when" className={inputCls} value={condSubject}
              onChange={(e) => { setCondSubject(e.target.value); setCondValue('') }}>
              <option value="">— pick a field —</option>
              {Object.entries(subjectSlots).map(([k, s]) => (
                <option key={k} value={k}>{s.label ?? k}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="sr-only">comparison</span>
            <select aria-label="comparison" className={inputCls} value={condOp}
              onChange={(e) => setCondOp(e.target.value as '==' | '!=')}>
              <option value="==">is</option>
              <option value="!=">is not</option>
            </select>
          </label>
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5">
              <span className="sr-only">value</span>
              {subjectSlot?.options?.length ? (
                <select aria-label="value" className={inputCls} value={condValue}
                  onChange={(e) => setCondValue(e.target.value)}>
                  <option value="">— pick a value —</option>
                  {subjectSlot.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input aria-label="value" className={`${inputCls} w-32`} value={condValue}
                  onChange={(e) => setCondValue(e.target.value)} placeholder="value" />
              )}
            </label>
            {condValueHasApostrophe && (
              <p className="text-destructive">Avoid apostrophes here — they break the condition.</p>
            )}
          </div>
        </div>
      )}

      {type === 'enum' && (
        <label className="flex items-center gap-1.5">
          Choices
          <input
            aria-label="choices"
            className={`${inputCls} w-64`}
            value={choicesText}
            onChange={(e) => setChoicesText(e.target.value)}
            onBlur={() =>
              setOptions(choicesText.split(',').map((s) => s.trim()).filter(Boolean))
            }
          />
          <span className="text-muted-foreground">(comma-separated)</span>
        </label>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {languages.map((lang) => (
          <label key={lang} className="block">
            <span className="font-mono uppercase text-muted-foreground">
              ask in {lang}
            </span>
            <textarea
              aria-label={`ask in ${lang}`}
              rows={2}
              className={`${inputCls} mt-1 w-full`}
              value={asks[lang] ?? ''}
              onChange={(e) => setAsks((a) => ({ ...a, [lang]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <label className="block">
        Exact instructions to the AI (optional)
        <input aria-label="instructions to the AI" className={`${inputCls} mt-1 w-full`}
          value={promptHint} onChange={(e) => setPromptHint(e.target.value)} />
      </label>

      <div className="flex justify-end gap-2">
        <button type="button" className="rounded-sm px-3 py-1.5 font-medium text-foreground hover:bg-muted" onClick={reset}>
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          className="rounded-sm bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          onClick={submit}
        >
          Add
        </button>
      </div>
    </div>
  )
}
